import { Avatar, Box, Button, CircularProgress, FormControl, TextField, Typography, useTheme } from '@mui/material'
import { useUserStore } from '../../store/user.store'
import React, { useEffect, useRef, useState } from 'react'

import TryAgainMessageBlock from '../../components/common/TryAgainMessageBlock'
import DataLoadingSpinner from '../../components/common/DataLoadingSpinner'
import BlockMessage from '../../components/common/BlockMessage'
import searchUsersImg from '../../assets/search-users.svg'
import { debounce, getTimePassedFrom, getTimePassedFromDetails, Icons } from '../../utils/utilities'
import api from '../../service/api'
import CustomChip from '../../components/common/CustomChip'
import axios from 'axios'
import { toast } from 'react-toastify'
import { useNavigate } from 'react-router-dom'

const UserBlock = ( { user } ) => {

    const [fetchingImpersonationData, setFetchingImpersonationData] = useState( false )
    const [profileImg, setProfileImg] = useState( null )

    const { border, layout } = useTheme()

    const impersonate = async ( type ) => {
        try {
            setFetchingImpersonationData( true )
            const { data: { data } } = await api.audit.impersonate( { user_id: user.session_id, impersonation_type: type } )
            if ( data.impersonation_type === "duplicate" ) {
                if ( data.token )
                    window.open( `/impersonation/duplicate/${data.token}?type=${type}` )
                else
                    toast( `Impersonate token for ${user.name} is not found! Please check if the user has logged in to alive within last 5 hours.` )
            } else {
                if ( data.erp_login_token )
                    window.open( `/impersonation/erplogin/${data.erp_login_token}?type=${type}` )
                else
                    toast( `Impersonate token for ${user.name} is not found! Please check if the user has logged in to alive within last 5 hours.` )
            }
        } catch ( err ) {
            console.log( err )

            toast( err?.response?.data?.message || `Something went wrong while trying to impersonate ${user.name}` )
        } finally {
            setFetchingImpersonationData( false )
        }
    }

    useEffect( () => {

        const getProfileImg = async () => {
            try {
                if ( user.user_role !== 'STUDENT' ) {
                    const { data } = await axios.get( `https://acerp.acharyaerptech.in/api/employee/employeeDetailsFileDownload?fileName=${user.image_path}`, { responseType: "blob" } )
                    if ( data ) {
                        const url = URL.createObjectURL( data )
                        setProfileImg( url )
                    }
                }
            } catch ( err ) {
                console.error( err.message )
            }
        }

        getProfileImg()
    }, [user] )

    return (
        <Box flexWrap="wrap" display="flex" justifyContent="space-between" gap={layout.sectionsGap} alignItems="center" key={user.session_id} padding="10px" border={border[1]} borderRadius={layout.borderRadius}>
            <Box display="flex" alignItems={{ md: "center", xs: "flex-start" }} gap={layout.cardsGap}>
                <Avatar alt={`PROFILE-${user.id}`} src={profileImg} sx={{ width: "30px", height: "30px" }}>{( user.name || user.student_name || user.employee_name || user.username )?.at( 0 )?.toUpperCase()}</Avatar>
                <Box>
                    <Typography gutterBottom variant='subtitle2' flexDirection={{ md: "row", xs: "column-reverse" }} display="flex" gap={{ md: layout.cardsGap, xs: `calc(${layout.cardsGap} / 2)` }} component="h3">
                        {( user.name || user.student_name || user.employee_name || user.username )}
                        <Box marginTop={{ md: "-2px", xs: "0" }} display="inline-block">
                            <CustomChip color={getTimePassedFromDetails( user.last_login ).h >= 5 ? "error" : "success"} tooltipTitle='Last Login' value={`Last login: ${getTimePassedFromDetails( user.last_login ).displayString}`} />
                        </Box>
                    </Typography>
                    <Box display="flex" gap={`calc(${layout.cardsGap} / 2)`} alignItems="center" flexWrap="wrap">
                        <CustomChip tooltipTitle='Role' value={user.user_role} />
                        {user.user_role !== 'STUDENT' && <CustomChip tooltipTitle='Employee Code' value={user.empcode} />}
                        {user.user_role === 'STUDENT' && <CustomChip tooltipTitle='Student AUID' value={user.session_id} />}
                        <CustomChip tooltipTitle='Institute' value={user.institute} />
                        {user.user_role !== 'STUDENT' && <CustomChip tooltipTitle='Department' value={user.branch_name} />}
                        {user.user_role === 'STUDENT' && <CustomChip tooltipTitle='Course' value={user.course_short_name} />}
                        {user.user_role === 'STUDENT' && <CustomChip tooltipTitle='Branch' value={user.course_branch_name} />}
                        {user.user_role === 'STUDENT' && <CustomChip value={`Year/Sem: ${user.current_year}/${user.current_sem || "NA"}`} />}
                    </Box>
                </Box>
            </Box>
            <Box display="flex" gap={layout.cardsGap} alignItems="center">
                <Button onClick={() => impersonate( "duplicate" )} disabled={getTimePassedFromDetails( user.last_login ).h >= 5 || fetchingImpersonationData} startIcon={fetchingImpersonationData ? <CircularProgress size={20} /> : Icons.default.Impersonation} variant='contained' disableElevation size='small' sx={{ width: "max-content", flexShrink: 0, flexGrow: { md: "initial", xs: 1 } }} >Impersonate (Duplicate LMS)</Button>
                <Button onClick={() => impersonate( "erplogin" )} startIcon={fetchingImpersonationData ? <CircularProgress size={20} /> : Icons.default.Impersonation} variant='outlined' size='small' sx={{ width: "max-content", flexShrink: 0, flexGrow: { md: "initial", xs: 1 } }} >Impersonate (ERP Login)</Button>
            </Box>
        </Box>
    )
}

const Impersonation = () => {

    const [loadingUsers, setLoadingUsers] = useState( false )
    const [searchText, setSearchText] = useState( "" )
    const [usersFound, setUsersFound] = useState( [] )
    const [error, setError] = useState( null )

    const { layout, palette } = useTheme()
    const searchInputRef = useRef()

    const fetchUsers = async ( searchText ) => {
        try {
            if ( searchText.length >= 5 ) {
                setLoadingUsers( true )
                const { data } = await api.logging.filterUsers( { filterText: searchText } )
                setUsersFound( data.data )
            } else {
                setUsersFound( [] )
            }
            setError( null )
        } catch ( err ) {
            console.log( err )
            setError( { code: err?.response?.status || 500, message: err?.response?.data?.message || "Something went wrong while searching users matching the search text." } )
        } finally {
            setLoadingUsers( false )
        }
    }

    const searchUsers = debounce( "searchUsers", fetchUsers, 1000 )

    const handleUserSearch = ( event ) => {
        try {
            setSearchText( event.target.value )
            searchUsers( event.target.value )
        } catch ( err ) {
            console.log( err )
        }
    }

    return (
        <Box flexGrow={1} padding={layout.pagePadding} overflow="auto" display="flex" flexDirection="column">
            <Box flexGrow={1} display="flex" flexDirection="column" gap={layout.sectionsGap} >
                <Box display="flex" flexDirection={{ xs: "column", sm: "row" }} justifyContent="space-between" gap="20px" alignItems={{ sm: "center", xs: "stretch" }}>
                    <Box flexGrow="1">
                        <Typography variant="h1" fontWeight="600" component="h1" fontSize="14px">Impersonation</Typography>
                        <Typography variant="subtitle2" fontSize="12px" color='textSecondary' >See how users see their pages for debug</Typography>
                    </Box>
                </Box>

                <Box display="flex" gap={layout.cardsGap} flexDirection="column">
                    <FormControl fullWidth>
                        {/* <Typography htmlFor='user-search' variant='subtitle2' display="block" component="label" color={palette.form.labelColor}>Search user</Typography> */}
                        <TextField
                            placeholder='Search user by User ID, Employee ID or E-mail.'
                            fullWidth
                            inputRef={searchInputRef}
                            autoFocus
                            id="user-search"
                            size='small'
                            onChange={handleUserSearch}
                            type={'text'}
                            slotProps={{
                                input: {
                                    startAdornment: Icons.default.SearchIcon,
                                    sx: {
                                        gap: "10px"
                                    }
                                }
                            }}
                        />
                    </FormControl>

                    {usersFound.length > 0 && <Box display="flex" flexDirection="column" gap={layout.cardsGap}>
                        {usersFound.map( user => {
                            return (
                                <UserBlock key={user.session_id} user={user} />
                            )
                        } )}
                    </Box>}

                    {!error && usersFound.length === 0 && <Box borderRadius={layout.borderRadius} bgcolor={palette.contentBg} padding="20px" gap={layout.cardsGap} display="flex" alignItems="center" justifyContent="center" flexDirection="column">
                        <Box component="img" src={searchUsersImg} height="200px" />
                        {!loadingUsers && searchText.length >= 5 && <Typography textAlign="center" variant='subtitle2'> No users found for the search term  </Typography>}
                        {!loadingUsers && searchText && searchText.length < 5 && <Typography textAlign="center" variant='subtitle2'>Type <Typography textAlign="center" variant='subtitle2' color='customThemeColor'>{5 - searchText.length} more character{( 5 - searchText.length ) > 1 ? "s" : ""}</Typography> to begin the search. </Typography>}
                        {!loadingUsers && !searchText && <Typography textAlign="center" variant='subtitle2'>Type <Typography textAlign="center" variant='subtitle2' color='customThemeColor'>atleast 5 characters</Typography> to start searching users in the above field.</Typography>}
                        {loadingUsers && <DataLoadingSpinner waitingMessage="Searching users please wait..." />}
                    </Box>}
                </Box>
                {error && <TryAgainMessageBlock
                    err={error.message}
                    getData={() => fetchUsers( searchInputRef.current.value )}
                    code={error.code}
                    loading={loadingUsers}
                />}
            </Box>
        </Box>
    )
}

export default Impersonation
